import { SupportedLanguages } from '@constants/constants.type';
import { MusicGenre } from '@domain/music-genre';
import { Vibe } from '@domain/vibe';

export const COMPANY_SETTINGS_COUNTRY_ID = 'id';
export const COMPANY_SETTINGS_COUNTRY_NAME = 'name';
export const COMPANY_SETTINGS_COUNTRY_ISO_CODE = 'isoCode';

export interface CompanySettingsCountry {
  [COMPANY_SETTINGS_COUNTRY_ID]: string;
  [COMPANY_SETTINGS_COUNTRY_NAME]: string;
  [COMPANY_SETTINGS_COUNTRY_ISO_CODE]: string;
}

export const COMPANY_VAT_NUMBER_COUNTRY_CODE = 'countryCode';
export const COMPANY_VAT_NUMBER_NUMBER = 'number';

export const COMPANY_BANKING_DETAILS_BANK_NUMBER = 'bankNumber';
export const COMPANY_BANKING_DETAILS_BANK_CODE = 'bankCode';

export const COMPANY_BILLING_INFO_LEGAL_NAME = 'legalName';
export const COMPANY_BILLING_INFO_REGISTERED_ADDRESS = 'registeredAddress';
export const COMPANY_BILLING_INFO_POSTCODE = 'postcode';
export const COMPANY_BILLING_INFO_CITY = 'city';
export const COMPANY_SETTINGS_COUNTRY = 'country';
export const COMPANY_BILLING_INFO_COUNTRY = 'billingCountry';
export const COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON = 'isIndividualPerson';
export const COMPANY_BILLING_INFO_VAT_NUMBER = 'vatNumber';
export const COMPANY_BILLING_INFO_CODICE_FISCALE = 'codiceFiscale';
export interface CompanyBillingInfo {
  [COMPANY_BILLING_INFO_LEGAL_NAME]?: string;
  [COMPANY_BILLING_INFO_REGISTERED_ADDRESS]?: string;
  [COMPANY_BILLING_INFO_POSTCODE]?: string;
  [COMPANY_BILLING_INFO_CITY]?: string;
  [COMPANY_BILLING_INFO_CODICE_FISCALE]?: string;
  [COMPANY_SETTINGS_COUNTRY]?: CompanySettingsCountry;
  [COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]?: boolean;
  [COMPANY_BILLING_INFO_VAT_NUMBER]?: string;
}

export const COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME = 'accountHolderName';
export const COMPANY_BANKING_DETAILS_ACCOUNT_NUMBER = 'accountNumber';
export const COMPANY_BANKING_DETAILS_COUNTRY = 'bankingCountry';

export interface CompanyBankingDetails {
  [COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME]?: string;
  [COMPANY_BANKING_DETAILS_BANK_NUMBER]?: string;
  [COMPANY_BANKING_DETAILS_BANK_CODE]?: string;
  [COMPANY_SETTINGS_COUNTRY]?: CompanySettingsCountry;
}
export const COMPANY_FINANCE_NOTIFICATIONS_PHONE = 'phone';
export const COMPANY_FINANCE_NOTIFICATIONS_EMAILS = 'emails';

export interface CompanyFinanceNotifications {
  [COMPANY_FINANCE_NOTIFICATIONS_PHONE]?: string;
  [COMPANY_FINANCE_NOTIFICATIONS_EMAILS]?: string[];
}

export const COMPANY_SETTING_ID = 'id';
export const COMPANY_SETTING_PUBLIC_NAME = 'publicName';
export const COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS = 'customerSupportEmails';
export const COMPANY_SETTING_BILLING_INFO = 'billingInfo';
export const COMPANY_SETTING_BANKING_DETAILS = 'bankingDetails';
export const COMPANY_SETTING_FINANCE_NOTIFICATIONS = 'financeNotifications';
export const COMPANY_SETTING_LOGO_URL = 'logoUrl';
export const COMPANY_SETTING_PUBLIC_PROFILE = 'publicProfile';

export interface CompanySettings {
  [COMPANY_SETTING_ID]: string;
  [COMPANY_SETTING_LOGO_URL]?: string;
  [COMPANY_SETTING_PUBLIC_NAME]?: string;
  [COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]?: string[];
  [COMPANY_SETTING_BILLING_INFO]: CompanyBillingInfo;
  [COMPANY_SETTING_BANKING_DETAILS]: CompanyBankingDetails;
  [COMPANY_SETTING_FINANCE_NOTIFICATIONS]: CompanyFinanceNotifications;
  /**
   * Optional so a fixture that only exercises billing does not have to carry the whole block, and
   * so the form renders the same way with the organizer flag off whatever the GET returns.
   */
  [COMPANY_SETTING_PUBLIC_PROFILE]?: CompanyPublicProfile;
}

/**
 * Organizer public profile — the fields the Public info block gains behind the
 * `organizer-public-profile` flag. Declared here in one go, ahead of the tickets that render each
 * control, so four parallel branches do not collide in this file and in the form config.
 *
 * These are the FRONT-END form keys, and they do not mirror the API: the whole block arrives and
 * leaves nested under `publicProfile`, the base description lives in `translations[defaultLocale]`
 * rather than in a field of its own, and `areTranslationsEnabled` exists only on the form. The
 * mapping in the form's `config/utils` owns the translation between the two.
 */
export const COMPANY_SETTING_DESCRIPTION = 'description';
export const COMPANY_SETTING_DESCRIPTION_TRANSLATIONS = 'descriptionTranslations';
export const COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED = 'areTranslationsEnabled';
/** Not a form key: drives the translations block's base language, it is never edited directly. */
export const COMPANY_SETTING_DEFAULT_LOCALE = 'defaultLocale';
export const COMPANY_SETTING_CITIES = 'cities';
export const COMPANY_SETTING_MUSIC_GENRES = 'musicGenres';
export const COMPANY_SETTING_VIBES = 'vibes';
export const COMPANY_SETTING_IS_PUBLIC_PROFILE = 'isPublicProfile';
/** Not a form key: server-derived and read-only, and not rendered anywhere in the mock. */
export const COMPANY_SETTING_SLUG = 'slug';

/** Mirrors of the server-side guards. The API enforces all of them regardless of the form. */
export const COMPANY_PUBLIC_PROFILE_MAX_MUSIC_GENRES = 3;
export const COMPANY_PUBLIC_PROFILE_MAX_VIBES = 3;
export const COMPANY_PUBLIC_PROFILE_MAX_DESCRIPTION_LENGTH = 1000;

/**
 * Per-locale description. Only the locales that carry a translation are present — the base
 * locale's copy lives in `COMPANY_SETTING_DESCRIPTION`, not in here.
 */
export type CompanyDescriptionTranslations = Partial<Record<SupportedLanguages, string>>;

/**
 * The API's per-locale descriptions, base locale included. Keyed by a plain string rather than
 * `SupportedLanguages` because the company can hold a locale the Pro UI does not offer, and a
 * narrowed key would make that row unreadable instead of merely unrenderable.
 */
export type CompanyApiTranslations = Record<string, { description: string }>;

export interface CompanyImage {
  id: string;
  url: string;
  orderPosition: number;
}

/** The `cities` entry of the public profile — the API's lite city, not the full `CityListItem`. */
export interface CompanyProfileCity {
  id: string;
  legacyId: number;
  name: string;
  slug: string;
}

/**
 * The `publicProfile` block of the PATCH body. Every key is optional and only the ones present are
 * touched, which is what keeps a Region edit from clearing the description. `translations` is the
 * exception: it is sent whole, because a locale is cleared by sending it blank rather than by
 * omitting it, and omitting one would leave a stale translation the form no longer shows.
 *
 * `defaultLocale` is absent on purpose: the backend derives it from the base locale of the first
 * write and pins it from then on, so sending one would only let two editors fight over it.
 */
export interface CompanyPublicProfileRequest {
  isPublicProfile?: boolean;
  translations?: CompanyApiTranslations;
  cities?: string[];
  musicGenres?: string[];
  vibes?: string[];
}

/** The `publicProfile` block of `GET /v1/nightgraph/companies/{companyId}`. */
export interface CompanyPublicProfile {
  slug: string | null;
  isPublicProfile: boolean;
  defaultLocale: string | null;
  translations: CompanyApiTranslations;
  cities: CompanyProfileCity[];
  musicGenres: MusicGenre[];
  vibes: Vibe[];
  images: CompanyImage[];
}

export const COMPANY_SETTINGS_IMAGE_INPUT_NAME = 'logoUrl';

export interface CompanySettingsFormTypes {
  [COMPANY_SETTINGS_IMAGE_INPUT_NAME]: string;
  [COMPANY_SETTING_PUBLIC_NAME]: string;
  [COMPANY_SETTING_CUSTOMER_SUPPORT_EMAILS]: string;
  /**
   * Organizer public profile. Optional on the form type because the whole block is behind
   * `organizer-public-profile`: with the flag off none of these keys are registered, which is what
   * keeps the existing Save button's dirty state untouched.
   */
  [COMPANY_SETTING_DESCRIPTION]?: string;
  [COMPANY_SETTING_DESCRIPTION_TRANSLATIONS]?: CompanyDescriptionTranslations;
  [COMPANY_SETTING_ARE_TRANSLATIONS_ENABLED]?: boolean;
  [COMPANY_SETTING_CITIES]?: string[];
  [COMPANY_SETTING_MUSIC_GENRES]?: string[];
  [COMPANY_SETTING_VIBES]?: string[];
  [COMPANY_SETTING_IS_PUBLIC_PROFILE]?: boolean;
  [COMPANY_BILLING_INFO_COUNTRY]?: string;
  [COMPANY_BANKING_DETAILS_COUNTRY]?: string;
  [COMPANY_SETTING_BILLING_INFO]: {
    [COMPANY_BILLING_INFO_IS_INDIVIDUAL_PERSON]: string;
    [COMPANY_BILLING_INFO_LEGAL_NAME]: string;
    [COMPANY_BILLING_INFO_REGISTERED_ADDRESS]: string;
    [COMPANY_BILLING_INFO_POSTCODE]: string;
    [COMPANY_BILLING_INFO_CITY]: string;
    [COMPANY_BILLING_INFO_VAT_NUMBER]: string;
    [COMPANY_BILLING_INFO_CODICE_FISCALE]: string;
  };
  [COMPANY_SETTING_BANKING_DETAILS]: {
    [COMPANY_BANKING_DETAILS_ACCOUNT_HOLDER_NAME]?: string;
    [COMPANY_BANKING_DETAILS_BANK_NUMBER]?: string;
    [COMPANY_BANKING_DETAILS_BANK_CODE]?: string;
  };
  [COMPANY_SETTING_FINANCE_NOTIFICATIONS]: {
    [COMPANY_FINANCE_NOTIFICATIONS_PHONE]: string;
    [COMPANY_FINANCE_NOTIFICATIONS_EMAILS]: string;
  };
}
